import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useContent } from "@/context/ContentContext";
import { useLanguage } from "@/context/LanguageContext";
import { useT } from "@/lib/translations";
import { isValidEmail, useValidators, getPhoneError as getPhoneErrorFor } from "@/lib/contactValidators";
import LocalePhoneInput from "@/components/LocalePhoneInput";
import DonationFlow from "@/components/DonationFlow";
import { parsePrice } from "@/lib/pricingHelpers";
import {
  PawPrint, ChevronLeft, ChevronRight, Heart, Info, CreditCard, Building2,
  Copy, CheckCircle, Calendar, Upload, Send, Utensils, Stethoscope
} from "lucide-react";
import { trackConversion, trackFunnelStep } from "@/lib/analytics";

const TOTAL_STEPS = 4;
const DEFAULT_IBAN = "TR36 0001 5001 5800 7319 4625 42";
const DEFAULT_DERNEK_ADI = "Pati Bekçileri Yaşatma ve Koruma Derneği";

// Hardcoded fallback — CMS value from `pricing.guardian_min` overrides this.
const FALLBACK_GUARDIAN_MIN = 500;

export default function GuardianFormPage() {  const { dogId } = useParams();
  const navigate = useNavigate();
  const { t } = useContent();
  const L = useT();
  const { locale } = useLanguage();
  const [dog, setDog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Adım değişiminde form kartı doğrudan ekranın üstüne gelsin (ilk açılış hariç).
  const stepCardRef = useRef(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const el = stepCardRef.current;
    if (!el) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const id = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    return () => clearTimeout(id);
  }, [currentStep]);
  const [guardianId, setGuardianId] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [quickSupport, setQuickSupport] = useState(null); // { type, presetAmount, presetDonationType, presetTitle }
  const [fonzipEnabled, setFonzipEnabled] = useState(false);

  // Fonzip aktifken IBAN ile aylık talimat verilemiyor → ödeme yöntemi adımını atlıyoruz.
  const skipPaymentStep = fonzipEnabled;

  // CMS-driven Koruyucu Aile minimum (Fiyatlar tab in admin).
  const MIN_AMOUNT = parsePrice(t("pricing.guardian_min", ""), FALLBACK_GUARDIAN_MIN);

  const [formData, setFormData] = useState({
    amount: 0,
    customAmount: "",
    ad_soyad: "",
    telefon: "",
    email: "",
    payment_method: "",
    bank_name: "",
    transfer_date: new Date().toISOString().split("T")[0],
    transfer_amount: "",
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/fonzip/status`).then((r) => {
      if (!cancelled) setFonzipEnabled(!!r.data?.guardian_enabled);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchDog();
    // Funnel: guardian sign-up form opened (Meta InitiateCheckout equivalent)
    trackFunnelStep("begin_guardian", { dog_id: dogId, content_type: "guardian" });
  }, [dogId]);

  const fetchDog = async () => {
    try {
      const response = await axios.get(`${API}/dogs/${dogId}`);
      setDog(response.data.dog || response.data);
    } catch (error) {
      toast.error(L("Köpek bilgisi alınamadı"));
      navigate("/canlar");
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleCustomAmount = (val) => {
    const num = parseInt(val) || 0;
    setFormData(prev => ({ ...prev, customAmount: val, amount: num }));
  };

  const generateAciklamaKodu = () => {
    const dogName = (dog?.name || "KOPEK").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const telSon4 = formData.telefon.replace(/\D/g, "").slice(-4) || "0000";
    return `KAHU-KORUYUCU-${dogName}-${telSon4}`;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return true;
      case 2: {
        if (formData.amount < MIN_AMOUNT) return false;
        if (!formData.ad_soyad.trim()) return false;
        if (getPhoneErrorFor(formData.telefon, { required: true }, locale)) return false;
        if (formData.email && !isValidEmail(formData.email)) return false;
        return true;
      }
      case 3: return !!formData.payment_method;
      case 4: return true;
      default: return true;
    }
  };

  const handleCreateGuardian = async (methodOverride = null) => {
    setSubmitting(true);
    const method = methodOverride || formData.payment_method;
    try {
      const res = await axios.post(`${API}/guardians`, {
        dog_id: dogId,
        guardian_name: formData.ad_soyad,
        guardian_phone: formData.telefon,
        guardian_email: formData.email || null,
        monthly_amount: formData.amount,
        payment_method: method,
      });
      setGuardianId(res.data.guardian_id);
      setFormData(prev => ({ ...prev, payment_method: method, transfer_amount: String(prev.amount) }));
      // GA4/Meta Pixel conversion event — guardian signup started
      trackConversion("guardian_signup_completed", {
        guardian_id: res.data.guardian_id,
        dog_id: dogId,
        value: formData.amount,
        currency: "TRY",
      });
      setCurrentStep(4);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      console.error("guardian create failed", status, detail || error.message);
      toast.error(typeof detail === "string" && detail ? detail : `${L("Kayıt oluşturulamadı")}${status ? ` (${status})` : ""}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!guardianId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/guardians/${guardianId}/verify-payment`, {
        guardian_id: guardianId,
        bank_name: formData.bank_name || null,
        transfer_date: formData.transfer_date,
        amount: parseFloat(formData.transfer_amount) || formData.amount,
      });
      setCompleted(true);
    } catch (error) {
      toast.error(L("Ödeme bildirimi gönderilemedi"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 3) {
      handleCreateGuardian();
    } else if (currentStep === 2 && skipPaymentStep) {
      handleCreateGuardian("iyzico");
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(L("{label} kopyalandı!").replace("{label}", label));
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="spinner"></div></div>;
  }

  // Completed / Thank you screen
  if (completed) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="bg-card border border-border/50 shadow-lg" data-testid="guardian-success-card">
            <CardContent className="p-8 md:p-12 text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl" data-testid="guardian-success-title">
                {formData.payment_method === "bank_transfer"
                  ? L("Teşekkürler! Ödemeniz kontrol edilince güncellemeleriniz başlayacak.")
                  : L("Teşekkürler! Koruyucu aile desteğiniz başladı.")}
              </h1>
              <p className="text-muted-foreground">
                {L("Güncellemeler (varsa) WhatsApp üzerinden iletilecek.")}
              </p>
              {formData.payment_method === "iyzico" && (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl">
                  {L("Düzenli destek için her ay aynı tutarı gönderebilir veya bankanızdan düzenli havale talimatı verebilirsiniz.")}
                </p>
              )}

              {/* Upsell: one-time top-off donation (mama / veteriner) */}
              <div className="text-left border-t border-border/50 pt-6 mt-2">
                <p className="text-sm text-muted-foreground mb-1">
                  {L("Aylık taahhüdün başladı. Dilersen şu an tek seferlik bir katkıyla bugün {name} için sıcak bir tabak mama ya da bir veteriner ziyareti hediye edebilirsin.").replace("{name}", dog?.name || L("bir can"))}
                </p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-4 mb-2 font-medium">
                  {L("Anında Destek")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setQuickSupport({
                      type: "mama",
                      presetDonationType: "meal",
                      presetTitle: dog?.name ? L("{name} için Mama Desteği").replace("{name}", dog.name) : L("Genel Mama Desteği"),
                    })}
                    data-testid="guardian-thanks-quick-meal"
                    className="btn-pill w-full min-w-0 h-12 px-3 text-sm border-2 border-secondary/60 bg-white text-secondary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                  >
                    <Utensils className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-center leading-tight">{L("Mama Desteği")}</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setQuickSupport({
                      type: "vet",
                      presetDonationType: "vet_visit",
                      presetTitle: dog?.name ? L("{name} için Veteriner Desteği").replace("{name}", dog.name) : L("Genel Veteriner Desteği"),
                    })}
                    data-testid="guardian-thanks-quick-vet"
                    className="btn-pill w-full min-w-0 h-12 px-3 text-sm border-2 border-secondary/60 bg-white text-secondary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                  >
                    <Stethoscope className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-center leading-tight">{locale === "en" ? "Vet Support" : "Veteriner"}</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to={`/can/${dogId}`}>
                  <Button variant="outline" className="btn-pill w-full">{L("{name}'e Geri Dön").replace("{name}", dog?.name || "")}</Button>
                </Link>
                <Link to="/canlar">
                  <Button variant="outline" className="btn-pill w-full">{L("Diğer Canları Keşfet")}</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick top-off donation modal */}
        {quickSupport && (
          <DonationFlow
            open={!!quickSupport}
            onClose={() => setQuickSupport(null)}
            type={quickSupport.type}
            dogId={dogId}
            dogName={dog?.name || quickSupport.presetTitle}
            presetAmount={quickSupport.presetAmount}
            presetDonationType={quickSupport.presetDonationType}
            presetTitle={quickSupport.presetTitle}
            upsellVariant="adopt"
          />
        )}
      </PageShell>
    );
  }

  const totalSteps = skipPaymentStep ? TOTAL_STEPS - 1 : TOTAL_STEPS;
  const displayStep = skipPaymentStep && currentStep > 2 ? currentStep - 1 : currentStep;
  const progress = (displayStep / totalSteps) * 100;

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        {/* Dog Info */}
        {dog && (
          <div className="flex items-center gap-4 mb-6 p-4 bg-card rounded-xl border border-border/50">
            {dog.photo_url ? (
              <img src={dog.photo_url} alt={dog.name} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                <PawPrint className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div>
              <h2 className="font-heading text-xl">{L("{name} için Koruyucu Aile").replace("{name}", dog.name)}</h2>
              <p className="text-sm text-muted-foreground">{dog.breed || L("Karışık")}</p>
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{L("Adım {n} / {total}").replace("{n}", displayStep).replace("{total}", totalSteps)}</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card ref={stepCardRef} className="scroll-mt-20 bg-card border border-border/50 shadow-lg" data-testid="guardian-form-card">
          <CardContent className="p-6 md:p-8">
            {currentStep === 1 && <Step1Info dogName={dog?.name} />}
            {currentStep === 2 && <Step2AmountContact formData={formData} handleCustomAmount={handleCustomAmount} update={update} dogName={dog?.name} minAmount={MIN_AMOUNT} />}
            {currentStep === 3 && !skipPaymentStep && <Step3PaymentMethod formData={formData} update={update} />}
            {currentStep === 4 && formData.payment_method === "bank_transfer" && (
              <Step4BankTransfer
                formData={formData} update={update} dogName={dog?.name}
                aciklamaKodu={generateAciklamaKodu()} copyToClipboard={copyToClipboard}
                onVerify={handleVerifyPayment} submitting={submitting}
                onBack={() => setCurrentStep(skipPaymentStep ? 2 : 3)}
              />
            )}
            {currentStep === 4 && formData.payment_method === "iyzico" && (
              <Step4Iyzico formData={formData} guardianId={guardianId} onComplete={() => setCompleted(true)} onBack={() => setCurrentStep(skipPaymentStep ? 2 : 3)} backLabel={skipPaymentStep ? L("Geri") : L("Geri (Ödeme Yöntemi Değiştir)")} />
            )}

            {/* Navigation - sticky at bottom */}
            {!(currentStep === 4) && (
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-8 pt-6 border-t border-border/50">
                <Button
                  variant="outline"
                  onClick={() => currentStep === 1 ? navigate(`/can/${dogId}`) : setCurrentStep(prev => prev - 1)}
                  className="btn-pill w-full sm:w-auto"
                  data-testid="guardian-back-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {currentStep === 1 ? L("Vazgeç") : L("Geri")}
                </Button>

                <Button
                  onClick={handleNext}
                  disabled={!canProceed() || submitting}
                  className="btn-pill bg-primary text-primary-foreground w-full sm:w-auto whitespace-normal h-auto py-3 text-center leading-tight"
                  data-testid="guardian-next-btn"
                >
                  <span className="flex-1 min-w-0">
                    {submitting ? L("İşleniyor...") : currentStep === 1 ? L("Devam et") :
                     currentStep === 2 ? (skipPaymentStep
                       ? L("Aylık {amount} TL — Fonzip ile Devam Et").replace("{amount}", formData.amount)
                       : L("Aylık {amount} TL Destek Ol").replace("{amount}", formData.amount)) :
                     L("Devam et")}
                  </span>
                  <ChevronRight className="w-4 h-4 ml-2 flex-shrink-0" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  const L = useT();
  const { t } = useContent();
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <nav className="glass-nav sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="h-7 w-7 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-xl font-semibold">KAHU</span>
            </Link>
            <div className="text-sm text-muted-foreground">{t("guardian.form.title", "Koruyucu Aile Başvurusu")}</div>
          </div>
        </div>
      </nav>
      {t("guardian.form.description", "") && (
        <div className="max-w-4xl mx-auto px-4 md:px-8 pt-6">
          <p className="text-sm text-muted-foreground max-w-2xl">{t("guardian.form.description", "")}</p>
        </div>
      )}
      {children}
    </div>
  );
}

// Step 1 — Kısa Bilgilendirme
function Step1Info({ dogName }) {
  const L = useT();
  return (
    <div className="space-y-6" data-testid="guardian-step1">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Heart className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-2xl">{L("{name} için Koruyucu Aile Ol").replace("{name}", dogName)}</h2>
        </div>
      </div>

      <p className="text-muted-foreground leading-relaxed">
        {L("Koruyucu aile desteği maddidir (geçici yuva başvurusu değildir). Aylık desteğinle bakım bütçesine katkı olursun; biz de can dostunun güvenle bakıldığından emin oluruz.")}
      </p>

      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl">
          <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">{L("Ziyaretler uygunluk durumuna göre randevuyla yapılır.")}</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl">
          <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">{L("Desteğini istediğin zaman durdurabilirsin.")}</p>
        </div>
      </div>

      {/* Benefits box */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 space-y-3">
        <h4 className="font-medium">{L("Koruyucu Aile Olarak:")}</h4>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            {L("Talep ederek fotoğraf/video ve durum güncellemeleri alabilirsiniz")}
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            {L("Sağlık ve önemli gelişmeler hakkında bilgilendirilebilirsiniz")}
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            {L("Ziyaretler uygunluk durumuna göre randevuyla yapılır.")}
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            {L("Desteğinizi istediğiniz zaman durdurabilirsiniz.")}
          </li>
        </ul>
      </div>
    </div>
  );
}

// Step 2 — Tutar Seçimi + İletişim
function Step2AmountContact({ formData, handleCustomAmount, update, dogName, minAmount }) {
  const L = useT();
  const { locale } = useLanguage();
  const { getPhoneError, getEmailError } = useValidators();
  return (
    <div className="space-y-6" data-testid="guardian-step2">
      {/* Amount selection — sadece serbest tutar girişi. Preset yok. */}
      <div>
        <h2 className="font-heading text-2xl mb-1">{L("Aylık Destek Tutarı")}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {L("Aylık desteğini kendin belirle. Her katkı — büyüklüğü ne olursa olsun — bir cana dokunur.")}
        </p>

        <div className="space-y-2">
          <Label htmlFor="custom-amount-input">
            {L("Aylık destek tutarı")} <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">
              {locale === "en" ? "TL" : "₺"}
            </span>
            <Input
              id="custom-amount-input"
              type="number"
              value={formData.customAmount}
              onChange={(e) => handleCustomAmount(e.target.value)}
              className="pl-12 h-14 text-xl font-heading font-semibold"
              min={minAmount}
              placeholder={locale === "en" ? "Enter monthly amount" : "Aylık tutar girin"}
              data-testid="custom-amount-input"
            />
          </div>
          <p className="text-xs text-muted-foreground italic">
            {L("Minimum {amount} TL/ay. Sana uygun bir tutarı seçmekte özgürsün.").replace("{amount}", minAmount.toLocaleString(locale === "en" ? "en-US" : "tr-TR"))}
          </p>
        </div>

        {formData.amount > 0 && formData.amount < minAmount && (
          <Alert className="mt-3 border-destructive/50 bg-destructive/10">
            <AlertDescription className="text-destructive text-sm">{L("Minimum tutar {amount} TL'dir.").replace("{amount}", minAmount)}</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Contact */}
      <div className="border-t border-border/50 pt-6">
        <h3 className="font-heading text-lg mb-4">{L("İletişim Bilgileri")}</h3>
        <div className="space-y-4">
          <div>
            <Label>{L("Ad Soyad")} <span className="text-destructive">*</span></Label>
            <Input
              value={formData.ad_soyad}
              onChange={(e) => update("ad_soyad", e.target.value)}
              placeholder={L("Adınız Soyadınız")}
              className="mt-1.5"
              data-testid="guardian-ad-soyad"
            />
          </div>
          <div>
            <Label>{L("Telefon (WhatsApp)")} <span className="text-destructive">*</span></Label>
            <div className="mt-1.5">
              <LocalePhoneInput
                value={formData.telefon}
                onChange={(v) => update("telefon", v)}
                testid="guardian-telefon"
                invalid={!!(formData.telefon && getPhoneError(formData.telefon, { required: true }))}
              />
            </div>
            {formData.telefon && getPhoneError(formData.telefon, { required: true }) && (
              <p className="text-xs text-destructive mt-1.5" data-testid="guardian-telefon-error">
                {getPhoneError(formData.telefon, { required: true })}
              </p>
            )}
          </div>
          <div>
            <Label>{L("E-posta")} <span className="text-xs text-muted-foreground">{L("(opsiyonel)")}</span></Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder={L("ornek@email.com")}
              className="mt-1.5"
              data-testid="guardian-email"
            />
            {getEmailError(formData.email, { required: false }) && (
              <p className="text-xs text-destructive mt-1.5" data-testid="guardian-email-error">
                {getEmailError(formData.email, { required: false })}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3 — Ödeme Yöntemi Seçimi
function Step3PaymentMethod({ formData, update }) {
  const L = useT();
  const [fonzipEnabled, setFonzipEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/fonzip/status`).then((r) => {
      if (!cancelled) setFonzipEnabled(!!r.data?.guardian_enabled);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="guardian-step3">
      <div>
        <h2 className="font-heading text-2xl mb-1">{L("Ödeme Yöntemi Seç")}</h2>
        <p className="text-sm text-muted-foreground">{L("Aylık desteğini başlatmak için bir yöntem seç:")}</p>
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={() => update("payment_method", "iyzico")}
          className={`w-full flex items-center gap-4 p-5 rounded-xl border-2 text-left transition-all ${
            formData.payment_method === "iyzico"
              ? "border-primary bg-primary/10"
              : "border-muted hover:bg-muted/30"
          }`}
          data-testid="payment-iyzico"
        >
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <CreditCard className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-medium">{fonzipEnabled ? L("Kart ile Düzenli Öde (Fonzip)") : L("Kart ile Öde")}</p>
            <p className="text-sm text-muted-foreground">{fonzipEnabled ? L("Aylık talimatını Fonzip'in güvenli sayfasında ver.") : L("Hızlı, kolay ve anında onay.")}</p>
          </div>
          {formData.payment_method === "iyzico" && (
            <span className="ml-auto text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">{L("Önerilen")}</span>
          )}
        </button>

        <button
          type="button"
          onClick={() => update("payment_method", "bank_transfer")}
          className={`w-full flex items-center gap-4 p-5 rounded-xl border-2 text-left transition-all ${
            formData.payment_method === "bank_transfer"
              ? "border-primary bg-primary/10"
              : "border-muted hover:bg-muted/30"
          }`}
          data-testid="payment-bank-transfer"
        >
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">{L("Banka Havalesi / EFT (IBAN)")}</p>
            <p className="text-sm text-muted-foreground">{L("Banka uygulamandan düzenli havale talimatı verebilirsin.")}</p>
          </div>
        </button>
      </div>
    </div>
  );
}

// Step 4A — Bank Transfer IBAN Screen
function Step4BankTransfer({ formData, update, dogName, aciklamaKodu, copyToClipboard, onVerify, submitting, onBack }) {
  const L = useT();
  const [showVerifyForm, setShowVerifyForm] = useState(false);
  const { t } = useContent();
  const IBAN = t("donation.iban", DEFAULT_IBAN);
  const DERNEK_ADI = t("donation.dernek_adi", DEFAULT_DERNEK_ADI);

  return (
    <div className="space-y-6" data-testid="guardian-step4-bank">
      <div>
        <h2 className="font-heading text-2xl mb-1">{L("Banka Havalesi / EFT ile Aylık Destek")}</h2>
        <p className="text-sm text-muted-foreground">
          {L("Bankanızdan havale/EFT yapabilir veya her ay otomatik gitmesi için düzenli havale talimatı oluşturabilirsiniz.")}
        </p>
      </div>

      {/* IBAN info */}
      <div className="bg-muted/30 rounded-xl p-5 space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground">{L("Dernek adı ve hesap ismi")}</Label>
          <p className="font-medium text-sm mt-1">{DERNEK_ADI}</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">{L("IBAN")}</Label>
          <div className="flex items-center gap-2 mt-1">
            <p className="font-mono font-medium text-sm">{IBAN}</p>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(IBAN.replace(/\s/g, ""), L("IBAN"))} data-testid="copy-iban-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> {L("Kopyala")}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">{L("Açıklama kodu")}</Label>
          <div className="flex items-center gap-2 mt-1">
            <p className="font-mono font-medium text-sm">{aciklamaKodu}</p>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(aciklamaKodu, L("Açıklama kodu"))} data-testid="copy-code-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> {L("Kopyala")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {L("Lütfen havale açıklamasına bu kodu yazın. Böylece ödemenizi hızlıca eşleştiririz.")}
          </p>
        </div>
        <div className="text-xs text-muted-foreground bg-primary/5 p-3 rounded-lg">
          {L("Düzenli destek için bankanızdan düzenli havale talimatı oluşturabilirsiniz.")}
        </div>
      </div>

      {/* Ödeme Yaptım */}
      {!showVerifyForm ? (
        <Button
          className="w-full btn-pill bg-primary text-primary-foreground h-12"
          onClick={() => setShowVerifyForm(true)}
          data-testid="payment-done-btn"
        >
          <CheckCircle className="w-5 h-5 mr-2" /> {L("Ödeme Yaptım")}
        </Button>
      ) : (
        <div className="space-y-4 p-4 bg-muted/20 rounded-xl border border-border/50" data-testid="verify-form">
          <h3 className="font-medium">{L("Ödeme Doğrulama")}</h3>
          <div>
            <Label>{L("Banka adı")} <span className="text-xs text-muted-foreground">{L("(opsiyonel)")}</span></Label>
            <Input
              value={formData.bank_name}
              onChange={(e) => update("bank_name", e.target.value)}
              placeholder={L("Örn: Ziraat Bankası")}
              className="mt-1.5"
              data-testid="verify-bank-name"
            />
          </div>
          <div>
            <Label>{L("Tarih")} <span className="text-destructive">*</span></Label>
            <Input
              type="date"
              value={formData.transfer_date}
              onChange={(e) => update("transfer_date", e.target.value)}
              className="mt-1.5"
              data-testid="verify-date"
            />
          </div>
          <div>
            <Label>{L("Tutar")} ({L("TL")})</Label>
            <Input
              type="number"
              value={formData.transfer_amount}
              onChange={(e) => update("transfer_amount", e.target.value)}
              className="mt-1.5"
              data-testid="verify-amount"
            />
          </div>
          <Button
            className="w-full btn-pill bg-primary text-primary-foreground"
            onClick={onVerify}
            disabled={submitting || !formData.transfer_date}
            data-testid="verify-submit-btn"
          >
            {submitting ? L("Gönderiliyor...") : L("Gönder")}
            <Send className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Back button */}
      <Button variant="outline" className="btn-pill" onClick={onBack} data-testid="bank-back-btn">
        <ChevronLeft className="w-4 h-4 mr-2" /> {L("Geri")}
      </Button>
    </div>
  );
}

// Step 4B — iyzico / Fonzip Payment Screen
function Step4Iyzico({ formData, guardianId, onComplete, onBack, backLabel }) {
  const L = useT();
  const { locale } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [fonzipEnabled, setFonzipEnabled] = useState(false);
  const [handoff, setHandoff] = useState(null); // { url, ref_code } after Fonzip tab opened
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/fonzip/status`).then((r) => {
      if (!cancelled) setFonzipEnabled(!!r.data?.guardian_enabled);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fonzip webhook onayını yokla (bağışçı ödemeyi bitirince ekran kendiliğinden yeşile döner)
  useEffect(() => {
    if (!handoff?.ref_code || confirmed) return;
    const t = setInterval(() => {
      axios.get(`${API}/fonzip/intent/${handoff.ref_code}`).then((r) => {
        if (r.data?.confirmed) setConfirmed(true);
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [handoff, confirmed]);

  const handlePay = async () => {
    if (!guardianId) {
      toast.error(L("Koruyucu Aile kaydı bulunamadı"));
      return;
    }
    // Mobil Safari pop-up engellemesi: sekmeyi tıklama anında aç, adresi sonra ata.
    const win = window.open("", "_blank");
    if (win) {
      try {
        win.document.write(`<!doctype html><meta name="viewport" content="width=device-width,initial-scale=1"><title>Fonzip</title><body style="font-family:system-ui;padding:24px;text-align:center;color:#444">${L("Ödeme sayfası açılıyor...")}</body>`);
      } catch (e) { /* noop */ }
    }
    setLoading(true);
    try {
      const endpoint = fonzipEnabled ? "/fonzip/guardian/initiate" : "/iyzico/checkout/initialize";
      const body = fonzipEnabled
        ? {
            guardian_id: guardianId,
            amount: formData.amount,
            donor_name: formData.ad_soyad,
            donor_email: formData.email || null,
            donor_phone: formData.telefon,
          }
        : {
            donation_id: guardianId,
            amount: formData.amount,
            donor_name: formData.ad_soyad,
            donor_email: formData.email || `noreply+${formData.telefon.replace(/\D/g, "")}@kahu.com.tr`,
            donor_phone: formData.telefon,
          };
      const res = await axios.post(`${API}${endpoint}`, body);
      if (!res.data?.paymentPageUrl) {
        if (win) win.close();
        toast.error(L("Ödeme sayfası alınamadı"));
        return;
      }
      if (fonzipEnabled) {
        if (win) {
          win.location.href = res.data.paymentPageUrl;
        } else {
          window.open(res.data.paymentPageUrl, "_blank", "noopener");
        }
        setHandoff({ url: res.data.paymentPageUrl, ref_code: res.data.ref_code });
      } else {
        if (win) win.close();
        window.location.href = res.data.paymentPageUrl;
      }
    } catch (error) {
      if (win) win.close();
      toast.error(error.response?.data?.detail || L("Ödeme başlatılamadı"));
    } finally {
      setLoading(false);
    }
  };

  const nfmt = (n) => n.toLocaleString(locale === "en" ? "en-US" : "tr-TR");

  if (handoff) {
    return (
      <div className="space-y-5 text-center" data-testid="guardian-fonzip-handoff">
        <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto ${confirmed ? "bg-emerald-100" : "bg-primary/10"}`}>
          <CheckCircle className={`w-8 h-8 ${confirmed ? "text-emerald-600" : "text-primary"}`} />
        </div>
        <h2 className="font-heading text-2xl">
          {confirmed ? L("Düzenli desteğin onaylandı 💛") : L("Fonzip ödeme sayfası yeni sekmede açıldı")}
        </h2>
        {!confirmed && (
          <>
            <p className="text-sm text-muted-foreground">
              {L("Fonzip'te \"Aylık\" seçeneğini işaretle, tutarını ve kart bilgilerini gir. Ödemen tamamlanınca bu ekran otomatik güncellenir.")}
            </p>
            <div className="rounded-xl border bg-muted/40 p-4 text-left space-y-2">
              <p className="text-xs text-muted-foreground">
                {L("Eşleştirmeyi garantiye almak için Fonzip formunda buradaki e-postanı kullan:")} <strong>{formData.email || "—"}</strong>
              </p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{L("KAHU Referans Kodun")}</span>
                <button
                  type="button"
                  className="font-mono text-sm font-bold px-2 py-1 rounded bg-white border inline-flex items-center gap-1 hover:bg-primary/5"
                  onClick={() => { navigator.clipboard.writeText(handoff.ref_code || "").then(() => toast.success(L("Kopyalandı"))).catch(() => {}); }}
                  data-testid="guardian-fonzip-ref-code"
                >
                  {handoff.ref_code} <Copy className="w-3 h-3" />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">{L("Fonzip formunda \"Açıklama / Not\" alanı varsa bu kodu yazabilirsin (isteğe bağlı).")}</p>
            </div>
            {(
              <a href={handoff.url} target="_blank" rel="noreferrer noopener" className="block text-sm text-primary underline" data-testid="guardian-fonzip-open-link">
                {L("Sayfa açılmadıysa buraya tıkla →")}
              </a>
            )}
          </>
        )}
        <Button
          className="w-full btn-pill bg-primary text-primary-foreground h-12 font-semibold"
          onClick={onComplete}
          data-testid="guardian-fonzip-done-btn"
        >
          {confirmed ? L("Devam Et") : L("Ödemeyi Tamamladım")}
        </Button>
        {!confirmed && (
          <Button variant="outline" className="btn-pill" onClick={() => window.open(handoff.url, "_blank", "noopener")} data-testid="guardian-fonzip-reopen-btn">
            {L("Fonzip sayfasını yeniden aç")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 text-center" data-testid="guardian-step4-iyzico">
      <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
        <CreditCard className="w-8 h-8 text-primary" />
      </div>
      <h2 className="font-heading text-2xl">{fonzipEnabled ? L("Düzenli Ödeme (Fonzip)") : L("Kart ile Öde")}</h2>
      <p className="text-muted-foreground">
        {L("Aylık tutar:")} <strong>{nfmt(formData.amount)} {L("TL")}</strong>
      </p>
      <p className="text-xs text-muted-foreground">
        {fonzipEnabled
          ? L("Fonzip'in güvenli sayfası yeni sekmede açılır; \"Aylık\" seçeneğiyle kart bilgilerini tanımlarsın. Her ay otomatik tahsil edilir, dilediğin zaman iptal edebilirsin.")
          : L("Bu ay için ilk ödeme alınır; sonraki aylar için ay başında kibarca hatırlatma e-postası göndereceğiz.")}
      </p>

      <Button
        className="w-full btn-pill bg-primary text-primary-foreground h-12 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30"
        onClick={handlePay}
        disabled={loading}
        data-testid="iyzico-pay-btn"
      >
        {loading
          ? L("Yönlendiriliyor...")
          : fonzipEnabled
            ? L("{amount} TL Fonzip ile Düzenli Öde").replace("{amount}", nfmt(formData.amount))
            : L("{amount} TL Kartla Öde").replace("{amount}", nfmt(formData.amount))}
      </Button>

      <Button variant="outline" className="btn-pill" onClick={onBack} data-testid="iyzico-back-btn">
        <ChevronLeft className="w-4 h-4 mr-2" /> {backLabel || L("Geri (Ödeme Yöntemi Değiştir)")}
      </Button>
    </div>
  );
}
